import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  Loader2, Plus, GraduationCap, Sparkles, Trophy, Flame, Users, Coins,
  School, Crown, Star, BookOpen, ArrowRight, Calendar, Sun
} from 'lucide-react';
import Navbar from '@/components/Navbar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AVATAR_OPTIONS = [
  { key: 'lion', label: 'Lion', color: '#f59e0b' },
  { key: 'panda', label: 'Panda', color: '#374151' },
  { key: 'robot', label: 'Robot', color: '#3b82f6' },
  { key: 'fox', label: 'Fox', color: '#ea580c' },
  { key: 'dragon', label: 'Dragon', color: '#16a34a' },
  { key: 'unicorn', label: 'Unicorn', color: '#ec4899' },
  { key: 'owl', label: 'Owl', color: '#7c3aed' },
  { key: 'monkey', label: 'Monkey', color: '#a16207' },
  { key: 'tiger', label: 'Tiger', color: '#dc2626' },
  { key: 'penguin', label: 'Penguin', color: '#0ea5e9' },
];

const AvatarCircle = ({ avatar, size = 48 }) => {
  const opt = AVATAR_OPTIONS.find(o => o.key === avatar) || AVATAR_OPTIONS[0];
  const initial = opt.label.charAt(0);
  return (
    <div
      className="rounded-full flex items-center justify-center font-bold text-white shadow-md"
      style={{ width: size, height: size, backgroundColor: opt.color, fontSize: size * 0.4 }}
      data-testid={`avatar-${avatar}`}
    >
      {initial}
    </div>
  );
};

const BADGE_ICONS = {
  first_save: Star,
  streak_7: Flame,
  streak_30: Crown,
  got_my_turn: Trophy,
  class_hero: Sparkles,
  multi_clubber: Users,
  early_bird: Sun,
  super_saver: Coins,
};

export default function KidsHub() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [kids, setKids] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [activeTab, setActiveTab] = useState('kids');

  // Add Kid dialog
  const [addKidOpen, setAddKidOpen] = useState(false);
  const [kidForm, setKidForm] = useState({
    name: '', age: 8, grade: '', school_name: '', avatar: 'lion'
  });
  const [savingKid, setSavingKid] = useState(false);

  // Selected kid detail
  const [selectedKid, setSelectedKid] = useState(null);
  const [kidDetail, setKidDetail] = useState(null);

  // Create Club dialog
  const [createClubOpen, setCreateClubOpen] = useState(false);
  const [clubForm, setClubForm] = useState({
    name: '', school_name: '', grade: '',
    contribution_amount: 1, frequency: 'daily', max_kids: 30,
  });
  const [savingClub, setSavingClub] = useState(false);

  // Join Club dialog
  const [joinOpen, setJoinOpen] = useState(false);
  const [joinForm, setJoinForm] = useState({ club_code: '', kid_id: '' });
  const [joining, setJoining] = useState(false);

  // Selected club
  const [selectedClub, setSelectedClub] = useState(null);
  const [clubDetail, setClubDetail] = useState(null);
  const [contributing, setContributing] = useState(null); // kid_id being contributed for

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [kidsRes, clubsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/kids`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/kid-clubs`, { withCredentials: true }),
      ]);
      setKids(kidsRes.data.kids || []);
      setClubs(clubsRes.data.clubs || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateKid = async () => {
    if (!kidForm.name.trim()) { toast.error('Name is required'); return; }
    setSavingKid(true);
    try {
      await axios.post(`${BACKEND_URL}/api/kids`, kidForm, { withCredentials: true });
      toast.success(`${kidForm.name} added!`);
      setAddKidOpen(false);
      setKidForm({ name: '', age: 8, grade: '', school_name: '', avatar: 'lion' });
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add kid');
    } finally {
      setSavingKid(false);
    }
  };

  const handleViewKid = async (kid) => {
    setSelectedKid(kid);
    setKidDetail(null);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/kids/${kid.kid_id}`, { withCredentials: true });
      setKidDetail(res.data);
    } catch (e) {
      toast.error('Failed to load kid details');
    }
  };

  const handleCreateClub = async () => {
    if (!clubForm.name.trim() || !clubForm.school_name.trim()) {
      toast.error('Class name and school are required'); return;
    }
    setSavingClub(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/kid-clubs`, clubForm, { withCredentials: true });
      toast.success(`Class created! Code: ${res.data.club_code}`);
      setCreateClubOpen(false);
      setClubForm({ name: '', school_name: '', grade: '', contribution_amount: 1, frequency: 'daily', max_kids: 30 });
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create class');
    } finally {
      setSavingClub(false);
    }
  };

  const handleJoinClub = async () => {
    if (!joinForm.club_code.trim() || !joinForm.kid_id) {
      toast.error('Class code and kid are required'); return;
    }
    setJoining(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/kid-clubs/join`, joinForm, { withCredentials: true });
      toast.success(`Joined ${res.data.club_name}!`);
      setJoinOpen(false);
      setJoinForm({ club_code: '', kid_id: '' });
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to join class');
    } finally {
      setJoining(false);
    }
  };

  const handleViewClub = async (club) => {
    setSelectedClub(club);
    setClubDetail(null);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/kid-clubs/${club.club_id}`, { withCredentials: true });
      setClubDetail(res.data);
    } catch (e) {
      toast.error('Failed to load class details');
    }
  };

  const handleContribute = async (kid_id) => {
    if (!selectedClub) return;
    setContributing(kid_id);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/kid-clubs/${selectedClub.club_id}/contribute`,
        { kid_id },
        { withCredentials: true }
      );
      const kidName = kids.find(k => k.kid_id === kid_id)?.name || 'Your kid';
      let msg = `${kidName} earned ${res.data.coins_awarded} coins!`;
      if (res.data.badges_awarded?.length) {
        msg += ` New badges: ${res.data.badges_awarded.join(', ')}`;
      }
      if (res.data.payout_processed) {
        const p = res.data.payout_processed;
        toast.success(
          `BIG DAY! ${p.recipient_name} got $${p.amount.toFixed(2)} (Round ${p.round}${p.club_completed ? ' — class finished!' : ''})`,
          { duration: 8000 }
        );
      } else {
        toast.success(msg);
      }
      handleViewClub(selectedClub);
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Contribution failed');
    } finally {
      setContributing(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-rose-50 to-sky-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-rose-50 to-sky-50">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center gap-3 mb-2">
            <GraduationCap className="w-10 h-10 text-amber-600" />
            <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-stone-900">
              School Susu
            </h1>
          </div>
          <p className="text-lg text-stone-600">
            Saving + classmates + parent oversight = lifelong financial confidence.
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/school-pitch')}
            className="mt-3"
            data-testid="view-school-pitch-btn"
          >
            <BookOpen className="w-4 h-4 mr-2" />
            For Teachers & School Boards →
          </Button>
        </motion.div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="bg-white shadow-sm" data-testid="kids-hub-tabs">
            <TabsTrigger value="kids" data-testid="tab-my-kids">
              My Kids ({kids.length})
            </TabsTrigger>
            <TabsTrigger value="clubs" data-testid="tab-school-clubs">
              School Clubs ({clubs.length})
            </TabsTrigger>
          </TabsList>

          {/* MY KIDS TAB */}
          <TabsContent value="kids" className="mt-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-stone-800">Manage your kids</h2>
              <Dialog open={addKidOpen} onOpenChange={setAddKidOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-amber-500 hover:bg-amber-600" data-testid="add-kid-btn">
                    <Plus className="w-4 h-4 mr-2" /> Add a Kid
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Add a Kid Sub-profile</DialogTitle>
                    <DialogDescription>
                      You stay in control. Real money flows through your Mini Bank only.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label>Kid's Name</Label>
                      <Input
                        value={kidForm.name}
                        onChange={e => setKidForm({ ...kidForm, name: e.target.value })}
                        placeholder="e.g. Amari"
                        data-testid="kid-name-input"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label>Age</Label>
                        <Input
                          type="number" min={4} max={18}
                          value={kidForm.age}
                          onChange={e => setKidForm({ ...kidForm, age: parseInt(e.target.value) || 8 })}
                          data-testid="kid-age-input"
                        />
                      </div>
                      <div>
                        <Label>Grade</Label>
                        <Input
                          value={kidForm.grade}
                          onChange={e => setKidForm({ ...kidForm, grade: e.target.value })}
                          placeholder="Grade 4"
                          data-testid="kid-grade-input"
                        />
                      </div>
                    </div>
                    <div>
                      <Label>School (optional)</Label>
                      <Input
                        value={kidForm.school_name}
                        onChange={e => setKidForm({ ...kidForm, school_name: e.target.value })}
                        placeholder="Lincoln Elementary"
                        data-testid="kid-school-input"
                      />
                    </div>
                    <div>
                      <Label>Pick an Avatar</Label>
                      <div className="grid grid-cols-5 gap-2 mt-2">
                        {AVATAR_OPTIONS.map(a => (
                          <button
                            key={a.key}
                            onClick={() => setKidForm({ ...kidForm, avatar: a.key })}
                            className={`p-2 rounded-lg border-2 transition ${
                              kidForm.avatar === a.key ? 'border-amber-500 bg-amber-50' : 'border-stone-200 hover:border-stone-400'
                            }`}
                            data-testid={`avatar-pick-${a.key}`}
                          >
                            <AvatarCircle avatar={a.key} size={36} />
                            <div className="text-xs mt-1">{a.label}</div>
                          </button>
                        ))}
                      </div>
                    </div>
                    <Button
                      onClick={handleCreateKid}
                      disabled={savingKid}
                      className="w-full bg-amber-500 hover:bg-amber-600"
                      data-testid="save-kid-btn"
                    >
                      {savingKid ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                      Add Kid
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            {kids.length === 0 ? (
              <Card className="border-dashed border-2 border-stone-300">
                <CardContent className="text-center py-12">
                  <Users className="w-12 h-12 text-stone-400 mx-auto mb-3" />
                  <p className="text-stone-600 mb-3">No kids yet. Add your first one to get started.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {kids.map(kid => (
                  <motion.div
                    key={kid.kid_id}
                    whileHover={{ y: -3 }}
                    transition={{ duration: 0.2 }}
                  >
                    <Card
                      className="cursor-pointer hover:shadow-lg transition-all border-stone-200"
                      onClick={() => handleViewKid(kid)}
                      data-testid={`kid-card-${kid.kid_id}`}
                    >
                      <CardContent className="p-5">
                        <div className="flex items-center gap-3 mb-3">
                          <AvatarCircle avatar={kid.avatar} size={56} />
                          <div>
                            <div className="font-bold text-lg">{kid.name}</div>
                            <div className="text-sm text-stone-500">
                              Age {kid.age}{kid.grade && ` • ${kid.grade}`}
                            </div>
                          </div>
                        </div>
                        <div className="flex justify-between text-sm">
                          <div className="flex items-center gap-1">
                            <Coins className="w-4 h-4 text-amber-500" />
                            <span className="font-semibold">{kid.virtual_coins || 0}</span>
                            <span className="text-stone-500">coins</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Flame className="w-4 h-4 text-orange-500" />
                            <span className="font-semibold">{kid.streak_days || 0}</span>
                            <span className="text-stone-500">day streak</span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* SCHOOL CLUBS TAB */}
          <TabsContent value="clubs" className="mt-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-2">
              <h2 className="text-lg font-semibold text-stone-800">Class & School Clubs</h2>
              <div className="flex gap-2">
                <Dialog open={joinOpen} onOpenChange={setJoinOpen}>
                  <DialogTrigger asChild>
                    <Button variant="outline" data-testid="join-club-btn">
                      Join with Code
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Join a Class Club</DialogTitle>
                      <DialogDescription>
                        Enter the 6-character class code from your child's teacher or another parent.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div>
                        <Label>Class Code</Label>
                        <Input
                          value={joinForm.club_code}
                          onChange={e => setJoinForm({ ...joinForm, club_code: e.target.value.toUpperCase() })}
                          placeholder="A1B2C3"
                          maxLength={6}
                          className="font-mono text-lg uppercase"
                          data-testid="join-code-input"
                        />
                      </div>
                      <div>
                        <Label>Which Kid?</Label>
                        <Select value={joinForm.kid_id} onValueChange={v => setJoinForm({ ...joinForm, kid_id: v })}>
                          <SelectTrigger data-testid="join-kid-select">
                            <SelectValue placeholder="Pick a kid" />
                          </SelectTrigger>
                          <SelectContent>
                            {kids.map(k => (
                              <SelectItem key={k.kid_id} value={k.kid_id}>{k.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <Button
                        onClick={handleJoinClub}
                        disabled={joining || kids.length === 0}
                        className="w-full bg-rose-500 hover:bg-rose-600"
                        data-testid="confirm-join-btn"
                      >
                        {joining ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                        Join Class
                      </Button>
                      {kids.length === 0 && (
                        <p className="text-sm text-stone-500 text-center">
                          Add a kid first before joining a class.
                        </p>
                      )}
                    </div>
                  </DialogContent>
                </Dialog>

                <Dialog open={createClubOpen} onOpenChange={setCreateClubOpen}>
                  <DialogTrigger asChild>
                    <Button className="bg-rose-500 hover:bg-rose-600" data-testid="create-club-btn">
                      <Plus className="w-4 h-4 mr-2" /> Create Class
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Create a Class Club</DialogTitle>
                      <DialogDescription>
                        You become the Class Coordinator. You'll get a code to share.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div>
                        <Label>Class Name</Label>
                        <Input
                          value={clubForm.name}
                          onChange={e => setClubForm({ ...clubForm, name: e.target.value })}
                          placeholder="Mrs. Smith's Grade 4"
                          data-testid="club-name-input"
                        />
                      </div>
                      <div>
                        <Label>School</Label>
                        <Input
                          value={clubForm.school_name}
                          onChange={e => setClubForm({ ...clubForm, school_name: e.target.value })}
                          placeholder="Lincoln Elementary"
                          data-testid="club-school-input"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label>Contribution ($)</Label>
                          <Input
                            type="number" min={0.25} step={0.25}
                            value={clubForm.contribution_amount}
                            onChange={e => setClubForm({ ...clubForm, contribution_amount: parseFloat(e.target.value) || 1 })}
                            data-testid="club-amount-input"
                          />
                        </div>
                        <div>
                          <Label>Cadence</Label>
                          <Select value={clubForm.frequency} onValueChange={v => setClubForm({ ...clubForm, frequency: v })}>
                            <SelectTrigger data-testid="club-frequency-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="daily">Daily</SelectItem>
                              <SelectItem value="weekly">Weekly</SelectItem>
                              <SelectItem value="monthly">Monthly</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div>
                        <Label>Max Kids</Label>
                        <Select
                          value={String(clubForm.max_kids)}
                          onValueChange={v => setClubForm({ ...clubForm, max_kids: parseInt(v) })}
                        >
                          <SelectTrigger data-testid="club-size-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="20">20 (small group)</SelectItem>
                            <SelectItem value="30">30 (one class)</SelectItem>
                            <SelectItem value="100">100 (grade level)</SelectItem>
                            <SelectItem value="200">200 (whole school)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-900">
                        <strong>Heads up:</strong> ${clubForm.contribution_amount} × {clubForm.max_kids} kids = <strong>${(clubForm.contribution_amount * clubForm.max_kids).toFixed(2)}</strong> per round payout.
                      </div>
                      <Button
                        onClick={handleCreateClub}
                        disabled={savingClub}
                        className="w-full bg-rose-500 hover:bg-rose-600"
                        data-testid="save-club-btn"
                      >
                        {savingClub ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                        Create Class
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </div>

            {clubs.length === 0 ? (
              <Card className="border-dashed border-2 border-stone-300">
                <CardContent className="text-center py-12">
                  <School className="w-12 h-12 text-stone-400 mx-auto mb-3" />
                  <p className="text-stone-600 mb-3">No classes yet. Create one or join with a code.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {clubs.map(club => (
                  <motion.div key={club.club_id} whileHover={{ y: -3 }}>
                    <Card
                      className="cursor-pointer hover:shadow-lg transition-all"
                      onClick={() => handleViewClub(club)}
                      data-testid={`club-card-${club.club_id}`}
                    >
                      <CardHeader>
                        <div className="flex justify-between items-start">
                          <div>
                            <CardTitle className="text-lg">{club.name}</CardTitle>
                            <CardDescription>
                              {club.school_name} {club.grade && `• ${club.grade}`}
                            </CardDescription>
                          </div>
                          <Badge variant="secondary" className="font-mono">
                            {club.club_code}
                          </Badge>
                        </div>
                      </CardHeader>
                      <CardContent className="text-sm text-stone-600">
                        <div className="flex justify-between">
                          <span>${club.contribution_amount} {club.frequency}</span>
                          <span>{club.member_kid_ids?.length || 0} / {club.max_kids} kids</span>
                        </div>
                        <div className="mt-2 text-xs text-stone-500">
                          Round {club.current_round + 1} of {club.rotation_order?.length || club.max_kids}
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* KID DETAIL DIALOG */}
      <Dialog open={!!selectedKid} onOpenChange={o => { if (!o) { setSelectedKid(null); setKidDetail(null); } }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          {selectedKid && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-3">
                  <AvatarCircle avatar={selectedKid.avatar} size={64} />
                  <div>
                    <DialogTitle className="text-2xl">{selectedKid.name}</DialogTitle>
                    <DialogDescription>
                      Age {selectedKid.age}{selectedKid.grade && ` • ${selectedKid.grade}`}
                      {selectedKid.school_name && ` • ${selectedKid.school_name}`}
                    </DialogDescription>
                  </div>
                </div>
              </DialogHeader>
              {!kidDetail ? (
                <Loader2 className="w-6 h-6 animate-spin mx-auto my-8" />
              ) : (
                <div className="space-y-4">
                  <div className="grid grid-cols-3 gap-3">
                    <div className="bg-amber-50 rounded-lg p-3 text-center">
                      <Coins className="w-5 h-5 text-amber-500 mx-auto mb-1" />
                      <div className="font-bold text-xl">{kidDetail.kid.virtual_coins || 0}</div>
                      <div className="text-xs text-stone-500">Coins</div>
                    </div>
                    <div className="bg-orange-50 rounded-lg p-3 text-center">
                      <Flame className="w-5 h-5 text-orange-500 mx-auto mb-1" />
                      <div className="font-bold text-xl">{kidDetail.kid.streak_days || 0}</div>
                      <div className="text-xs text-stone-500">Day Streak</div>
                    </div>
                    <div className="bg-emerald-50 rounded-lg p-3 text-center">
                      <Trophy className="w-5 h-5 text-emerald-500 mx-auto mb-1" />
                      <div className="font-bold text-xl">${(kidDetail.kid.total_payouts_received || 0).toFixed(2)}</div>
                      <div className="text-xs text-stone-500">Won</div>
                    </div>
                  </div>

                  <div>
                    <h3 className="font-semibold mb-2">Badges Earned ({kidDetail.badges.length})</h3>
                    {kidDetail.badges.length === 0 ? (
                      <p className="text-sm text-stone-500">No badges yet — keep saving!</p>
                    ) : (
                      <div className="grid grid-cols-4 gap-2">
                        {kidDetail.badges.map(b => {
                          const Icon = BADGE_ICONS[b.badge_key] || Star;
                          return (
                            <div key={b.award_id} className="bg-stone-50 rounded-lg p-2 text-center" data-testid={`badge-${b.badge_key}`}>
                              <Icon className="w-6 h-6 mx-auto text-amber-600 mb-1" />
                              <div className="text-xs font-semibold">{b.name}</div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  <div>
                    <h3 className="font-semibold mb-2">Active Clubs ({kidDetail.active_clubs.length})</h3>
                    {kidDetail.active_clubs.length === 0 ? (
                      <p className="text-sm text-stone-500">Not in any class yet.</p>
                    ) : (
                      <div className="space-y-2">
                        {kidDetail.active_clubs.map(c => (
                          <div key={c.club_id} className="bg-stone-50 rounded-lg p-3 flex justify-between items-center">
                            <div>
                              <div className="font-medium">{c.name}</div>
                              <div className="text-xs text-stone-500">{c.school_name} • Code {c.club_code}</div>
                            </div>
                            <Button size="sm" variant="outline" onClick={() => { setSelectedKid(null); handleViewClub(c); }}>
                              Open <ArrowRight className="w-3 h-3 ml-1" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* CLUB DETAIL DIALOG */}
      <Dialog open={!!selectedClub} onOpenChange={o => { if (!o) { setSelectedClub(null); setClubDetail(null); } }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          {selectedClub && (
            <>
              <DialogHeader>
                <DialogTitle className="text-2xl flex items-center gap-2">
                  <School className="w-6 h-6 text-rose-500" />
                  {selectedClub.name}
                </DialogTitle>
                <DialogDescription>
                  {selectedClub.school_name} • Code <span className="font-mono font-bold">{selectedClub.club_code}</span> •
                  ${selectedClub.contribution_amount} {selectedClub.frequency}
                </DialogDescription>
              </DialogHeader>
              {!clubDetail ? (
                <Loader2 className="w-6 h-6 animate-spin mx-auto my-8" />
              ) : (
                <div className="space-y-5">
                  {/* Participation meter */}
                  <div className="bg-gradient-to-r from-emerald-50 to-sky-50 rounded-lg p-4">
                    <div className="flex justify-between text-sm mb-1">
                      <span className="font-medium">Today's Class Participation</span>
                      <span className="font-bold">{clubDetail.participation_pct}%</span>
                    </div>
                    <div className="h-3 bg-white rounded-full overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${clubDetail.participation_pct}%` }}
                        className="h-full bg-gradient-to-r from-emerald-500 to-sky-500"
                      />
                    </div>
                    <p className="text-xs text-stone-500 mt-2">
                      {clubDetail.contributed_today.length} of {clubDetail.members.length} kids saved today
                    </p>
                  </div>

                  {/* Next recipient */}
                  {clubDetail.next_recipient && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-center gap-3">
                      <Trophy className="w-8 h-8 text-amber-600" />
                      <div className="flex-1">
                        <div className="text-sm text-stone-600">Up next for the payout:</div>
                        <div className="font-bold text-lg flex items-center gap-2">
                          <AvatarCircle avatar={clubDetail.next_recipient.avatar} size={28} />
                          {clubDetail.next_recipient.name}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-xs text-stone-500">Pot size</div>
                        <div className="font-bold">${(selectedClub.contribution_amount * clubDetail.members.length).toFixed(2)}</div>
                      </div>
                    </div>
                  )}

                  {/* My kids in this club — contribute buttons */}
                  {kids.filter(k => clubDetail.members.some(m => m.kid_id === k.kid_id)).length > 0 && (
                    <div>
                      <h3 className="font-semibold mb-2">Contribute Today</h3>
                      <div className="space-y-2">
                        {kids.filter(k => clubDetail.members.some(m => m.kid_id === k.kid_id)).map(k => {
                          const alreadyToday = clubDetail.contributed_today.includes(k.kid_id);
                          return (
                            <div key={k.kid_id} className="flex items-center gap-3 bg-stone-50 rounded-lg p-3">
                              <AvatarCircle avatar={k.avatar} size={40} />
                              <div className="flex-1">
                                <div className="font-medium">{k.name}</div>
                                <div className="text-xs text-stone-500">
                                  {alreadyToday ? 'Already contributed today' : `Contribute $${selectedClub.contribution_amount}`}
                                </div>
                              </div>
                              <Button
                                size="sm"
                                disabled={alreadyToday || contributing === k.kid_id}
                                onClick={() => handleContribute(k.kid_id)}
                                className="bg-emerald-500 hover:bg-emerald-600"
                                data-testid={`contribute-${k.kid_id}`}
                              >
                                {contributing === k.kid_id
                                  ? <Loader2 className="w-4 h-4 animate-spin" />
                                  : alreadyToday ? 'Done' : 'Save'}
                              </Button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Leaderboard */}
                  <div>
                    <h3 className="font-semibold mb-2 flex items-center gap-2">
                      <Crown className="w-4 h-4 text-amber-500" /> Class Leaderboard
                    </h3>
                    {clubDetail.leaderboard.length === 0 ? (
                      <p className="text-sm text-stone-500">No members yet.</p>
                    ) : (
                      <div className="space-y-1">
                        {clubDetail.leaderboard.slice(0, 10).map((m, i) => (
                          <div key={m.kid_id} className="flex items-center gap-3 bg-white rounded-lg p-2 border border-stone-100">
                            <span className="w-6 text-center font-bold text-stone-400">{i + 1}</span>
                            <AvatarCircle avatar={m.avatar} size={28} />
                            <span className="flex-1 font-medium">{m.name}</span>
                            <span className="text-sm flex items-center gap-1 text-orange-500">
                              <Flame className="w-3 h-3" /> {m.streak_days || 0}
                            </span>
                            <span className="text-sm flex items-center gap-1 text-amber-600">
                              <Coins className="w-3 h-3" /> {m.virtual_coins || 0}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
